import React from 'react';
import { ValvesProps } from '../types';

// Strie d'aria: [offset orizzontale %, deriva orizzontale px, ritardo s]
const AIR_STREAKS: [number, number, number][] = [
  [8, -8, 0],
  [22, -4, 0.3],
  [36, -1, 0.6],
  [50, 0, 0.15],
  [64, 1, 0.45],
  [78, 4, 0.75],
  [92, 8, 0.05],
];

// Sbuffi di fumo: [offset orizzontale px, deriva px, ritardo s]
const SMOKE_PUFFS: [number, number, number][] = [
  [-4, -10, 0],
  [3, 8, 0.35],
  [-2, -4, 0.7],
  [4, 12, 1.05],
  [0, 0, 1.4],
];

const AirFlow: React.FC = () => (
  <div className="valve-fx valve-air" aria-hidden="true">
    <div className="air-glow" />
    {AIR_STREAKS.map(([left, dx, delay], i) => (
      <span
        key={i}
        className="air-streak"
        style={{ left: `${left}%`, animationDelay: `${delay}s`, ['--dx' as string]: `${dx}px` }}
      />
    ))}
  </div>
);

const ExhaustSmoke: React.FC = () => (
  <div className="valve-fx valve-smoke" aria-hidden="true">
    {SMOKE_PUFFS.map(([x, dx, delay], i) => (
      <span
        key={i}
        className="smoke-puff"
        style={{ marginLeft: `${x - 11}px`, animationDelay: `${delay}s`, ['--dx' as string]: `${dx}px` }}
      />
    ))}
  </div>
);

const Valve: React.FC<{ label: string; isOpen: boolean; isRunning: boolean; position: 'left' | 'right'; effect: 'air' | 'smoke' }> = ({ label, isOpen, isRunning, position, effect }) => {
  const valvePositionClass = position === 'left' ? 'left-[20%]' : 'right-[20%]';
  return (
    <div className={`absolute top-0 ${valvePositionClass} w-6 flex flex-col items-center`}>
      <span className={`absolute -top-5 text-xs font-bold transition-colors duration-200 ${isOpen ? 'text-cyan-300 animate-pulse' : 'text-slate-400'}`}>{label}</span>
      <div
        className={`relative w-full h-10`}
      >
        {/* Aria e fumo solo a motore acceso: in modalità manuale la valvola si apre ma non c'è flusso */}
        {isOpen && isRunning && (effect === 'air' ? <AirFlow /> : <ExhaustSmoke />)}
        {/* Stem */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-6 bg-slate-600 rounded-t-sm" />
        {/* Head */}
        <div
          className={`absolute bottom-0 left-0 w-6 h-4 bg-gradient-to-t from-slate-600 to-slate-500 transition-transform duration-100 ease-in-out border-b-2 border-slate-400`}
          style={{
            clipPath: 'polygon(0 25%, 100% 25%, 80% 100%, 20% 100%)',
            transform: `translateY(${isOpen ? '8px' : '0px'})` // Moves down when open
          }}
        />
      </div>
    </div>
  );
};

const Valves: React.FC<ValvesProps> = ({ intakeOpen, exhaustOpen, isRunning }) => {
  // Posiziona il contenitore delle valvole in modo assoluto.
  // top-[13rem] allinea le valvole con la parte superiore della camera di combustione
  // (altezza iniettore 14rem - 1rem di sovrapposizione)
  return (
    // Larghezza fissa uguale alla camera (w-64), centrata: le valvole restano dentro il cilindro
    // anche se il contenitore si allarga (es. per le chip di stato).
    <div className="absolute top-[13rem] left-1/2 -translate-x-1/2 w-64 h-12 z-20">
        <Valve label="Entrata" isOpen={intakeOpen} isRunning={isRunning} position="left" effect="air" />
        <Valve label="Scarico" isOpen={exhaustOpen} isRunning={isRunning} position="right" effect="smoke" />
    </div>
  );
};

export default Valves;
